import React, { Component, ReactNode } from "react";
import i18next from "i18next";
import { Button } from "@telluscompany/design-system";

// Boundary é classe (único jeito de implementar `componentDidCatch`); como
// hooks não estão disponíveis, usamos o singleton `i18next.t(...)` direto —
// mesmo padrão de `authLoaders.ts`. A guard `isInitialized` cobre o
// cenário-limite em que o i18n ainda não foi `init()` (crash muito precoce
// na bootstrap do app, ou teste que renderiza este componente sem
// `import "@i18n/config"`): nesses casos o `t()` do i18next nem tenta
// resolver a key e devolve string vazia, então retornamos o texto PT-BR
// original como último fallback determinístico.
const t = (key: string, defaultValue: string): string => {
  if (!i18next.isInitialized) return defaultValue;
  return i18next.t(key, { defaultValue });
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Store error info for display
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full surface-primary p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-destructive/10 mb-4">
                <svg
                  className="h-6 w-6 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              <h2 className="text-heading-xl text-foreground mb-2">
                {t("errors.appBoundary.title", "Algo deu errado")}
              </h2>
              <p className="text-body-medium-regular text-muted-foreground mb-6">
                {t(
                  "errors.appBoundary.description",
                  "Ocorreu um erro inesperado. Por favor, tente novamente.",
                )}
              </p>

              {this.props.showErrorDetails && this.state.error && (
                <div className="bg-destructive/5 border border-destructive/20 rounded-md p-4 mb-6 text-left">
                  <h3 className="text-body-small-semibold text-destructive mb-2">
                    {t("errors.appBoundary.details", "Detalhes do Erro:")}
                  </h3>
                  <pre className="text-body-xsmall-regular text-destructive whitespace-pre-wrap break-all">
                    {this.state.error.message}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <details className="mt-2">
                      <summary className="text-body-xsmall-regular text-destructive/80 cursor-pointer">
                        {t("errors.appBoundary.stackTrace", "Stack Trace")}
                      </summary>
                      <pre className="text-body-xsmall-regular text-destructive/80 mt-1 whitespace-pre-wrap break-all">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="link" onClick={this.handleReset}>
                  {t("errors.appBoundary.retry", "Tentar Novamente")}
                </Button>
                <Button variant="link" onClick={this.handleReload}>
                  {t("errors.appBoundary.reload", "Recarregar Página")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
